import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Drawer, Input, Segmented } from 'antd';
import { DeleteOutlined, EyeOutlined, FilePdfTwoTone, PictureTwoTone, UploadOutlined } from '@ant-design/icons';
import dynamicFormsService from '../../services/dynamicFormsService';
import RichTextEditor from './RichTextEditor';
import LivePreview from './LivePreview';
import useElementWidth from './useElementWidth';
import { formAssetUrl } from './FormPageView';
import { asPublicField, previewData } from './previewData';

const TEXT_KEYS = ['title1', 'title2', 'title3', 'image_desc', 'form_details_title', 'form_details_alignment', 'form_details', 'downloadable_file_title'];
const FILE_KEYS = ['logo', 'image', 'downloadable_file'];

const textsFrom = (layout) => {
  const out = {};
  TEXT_KEYS.forEach((k) => { out[k] = layout?.[k] || ''; });
  out.form_details_alignment = layout?.form_details_alignment || 'left';
  return out;
};

const filesFrom = (layout) => {
  const out = {};
  FILE_KEYS.forEach((k) => { out[k] = layout?.[k] ? { kind: 'saved', path: layout[k] } : null; });
  return out;
};

const fileName = (path) => decodeURIComponent(String(path).split('/').pop() || '');

/** An image or document slot: shows what is there now, with replace and remove. */
const FileSlot = ({ id, label, hint, value, accept, image, onPick, onRemove, disabled, error }) => {
  const inputRef = useRef(null);
  const src = value ? (value.kind === 'new' ? value.url : formAssetUrl(value.path)) : null;
  const name = value ? (value.kind === 'new' ? value.file.name : fileName(value.path)) : null;
  return (
    <div className="dfw-field">
      <span className="dfw-label" id={`${id}-label`}>{label}</span>
      <div className={`dfw-slot${value ? ' has-file' : ''}${error ? ' is-error' : ''}`}>
        <span className="dfw-slot__thumb" aria-hidden="true">
          {image && src ? <img src={src} alt="" crossOrigin="anonymous" /> : image ? <PictureTwoTone twoToneColor="#667085" /> : <FilePdfTwoTone twoToneColor="#667085" />}
        </span>
        <span className="dfw-slot__text">
          <strong>{name || 'Nothing uploaded'}</strong>
          <small>{value?.kind === 'new' ? 'New, saved with the design' : hint}</small>
        </span>
        <span className="dfw-slot__actions">
          <Button size="small" icon={<UploadOutlined aria-hidden="true" />} onClick={() => inputRef.current?.click()} disabled={disabled} aria-describedby={`${id}-label`}>
            {value ? 'Replace' : 'Upload'}
          </Button>
          {value && (
            <Button size="small" type="text" danger icon={<DeleteOutlined aria-hidden="true" />} onClick={onRemove} disabled={disabled} aria-label={`Remove ${label.toLowerCase()}`} />
          )}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onPick(file);
          }}
        />
      </div>
      {error && <p className="dfw-error" role="alert">{error}</p>}
    </div>
  );
};

/**
 * The look of the public page: logo and titles, banner, instructions and the downloadable file.
 * Saves through POST /forms/:id/layout exactly as the old builder did (files as multipart,
 * an empty value clears a file).
 */
const DesignEditor = ({ formId, layout, onLayoutChange, canEdit = true, language, fields, liveUrl }) => {
  const { message } = App.useApp();
  const rootRef = useRef(null);
  const width = useElementWidth(rootRef);
  const [texts, setTexts] = useState(() => textsFrom(layout));
  const [files, setFiles] = useState(() => filesFrom(layout));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // preview URLs of unsaved uploads, freed when replaced, discarded or on leaving
  const filesRef = useRef(files);
  filesRef.current = files;
  const freeUrls = (list) => FILE_KEYS.forEach((k) => { if (list[k]?.kind === 'new') URL.revokeObjectURL(list[k].url); });
  useEffect(() => () => freeUrls(filesRef.current), []);

  const reset = () => {
    freeUrls(filesRef.current);
    setTexts(textsFrom(layout));
    setFiles(filesFrom(layout));
    setErrors({});
  };

  // reload when the saved layout changes (after saving, or another form)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reset, [layout]);

  const dirty = useMemo(() => {
    const savedTexts = textsFrom(layout);
    const savedFiles = filesFrom(layout);
    return TEXT_KEYS.some((k) => (texts[k] || '') !== (savedTexts[k] || ''))
      || FILE_KEYS.some((k) => (files[k]?.kind === 'new') || (!!files[k] !== !!savedFiles[k]));
  }, [texts, files, layout]);

  const setText = (key, value) => {
    setTexts((t) => ({ ...t, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const pickFile = (key, file) => {
    if (files[key]?.kind === 'new') URL.revokeObjectURL(files[key].url);
    setFiles((f) => ({ ...f, [key]: { kind: 'new', file, url: URL.createObjectURL(file) } }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const removeFile = (key) => {
    if (files[key]?.kind === 'new') URL.revokeObjectURL(files[key].url);
    setFiles((f) => ({ ...f, [key]: null }));
  };

  const save = async () => {
    const found = {};
    // the backend needs a logo and main title to create a layout, and never clears the main title
    if (!texts.title1.trim()) found.title1 = 'The page needs a main title.';
    if (!layout && !files.logo) found.logo = 'Add a logo before saving the first design.';
    setErrors(found);
    if (Object.keys(found).length) return;

    const formData = new FormData();
    FILE_KEYS.forEach((k) => {
      const v = files[k];
      if (v?.kind === 'new') formData.append(k, v.file);
      else if (!v) formData.append(k, '');
      // a saved file that is kept is left out, and the backend keeps it
    });
    TEXT_KEYS.forEach((k) => formData.append(k, (k === 'form_details_alignment' ? texts[k] || 'left' : texts[k]) || ''));

    try {
      setSaving(true);
      const response = await dynamicFormsService.updateLayout(formId, formData);
      if (!response.success) {
        message.error(response.error || 'Could not save the design');
        return;
      }
      await onLayoutChange();
      message.success('Design saved');
    } catch {
      message.error('Could not save the design');
    } finally {
      setSaving(false);
    }
  };

  const draftLayout = useMemo(() => {
    const out = { ...(layout || {}), ...texts };
    FILE_KEYS.forEach((k) => {
      const v = files[k];
      out[k] = v ? (v.kind === 'new' ? v.url : v.path) : null;
    });
    return out;
  }, [layout, texts, files]);

  const preview = useMemo(
    () => previewData({ language, layout: draftLayout, fields: fields.map(asPublicField) }),
    [language, draftLayout, fields]
  );

  const wide = width >= 1040;

  const form = (
    <fieldset className="dfw-design" disabled={!canEdit || saving}>
      {!canEdit && <p className="dfw-note">You can look at the design here. Changing it needs permission to manage client forms.</p>}

      <section className="dfw-group" aria-labelledby="dfw-d-top">
        <h3 className="dfw-group__title" id="dfw-d-top">Top of the page</h3>
        <FileSlot
          id="dfw-d-logo"
          label="Logo"
          hint="Shown large above the titles"
          value={files.logo}
          accept="image/*"
          image
          onPick={(f) => pickFile('logo', f)}
          onRemove={() => removeFile('logo')}
          disabled={!canEdit}
          error={errors.logo}
        />
        <div className="dfw-field">
          <label className="dfw-label" htmlFor="dfw-d-title1">Main title</label>
          <Input id="dfw-d-title1" value={texts.title1} onChange={(e) => setText('title1', e.target.value)} placeholder="e.g. Notte Bianca volunteers" status={errors.title1 ? 'error' : undefined} />
          {errors.title1 && <p className="dfw-error" role="alert">{errors.title1}</p>}
        </div>
        <div className="dfw-field">
          <label className="dfw-label" htmlFor="dfw-d-title2">Highlighted title <em>optional</em></label>
          <Input id="dfw-d-title2" value={texts.title2} onChange={(e) => setText('title2', e.target.value)} placeholder="e.g. 2026" />
          <p className="dfw-hint">Large and in the form's gold, under the main title.</p>
        </div>
        <div className="dfw-field">
          <label className="dfw-label" htmlFor="dfw-d-title3">Line under the titles <em>optional</em></label>
          <Input id="dfw-d-title3" value={texts.title3} onChange={(e) => setText('title3', e.target.value)} placeholder="e.g. Applications close on 30 June" />
        </div>
      </section>

      <section className="dfw-group" aria-labelledby="dfw-d-banner">
        <h3 className="dfw-group__title" id="dfw-d-banner">Banner <em>optional</em></h3>
        <FileSlot
          id="dfw-d-image"
          label="Banner image"
          hint="Shown in a card under the titles"
          value={files.image}
          accept="image/*"
          image
          onPick={(f) => pickFile('image', f)}
          onRemove={() => removeFile('image')}
          disabled={!canEdit}
        />
        <div className="dfw-field">
          <span className="dfw-label">Caption</span>
          <RichTextEditor
            value={texts.image_desc}
            onChange={(v) => setText('image_desc', v)}
            size="caption"
            align="center"
            minHeight={56}
            placeholder="Text under the banner image"
            aria-label="Banner caption"
            readOnly={!canEdit}
          />
          {!files.image && texts.image_desc && <p className="dfw-hint">The caption only shows with a banner image.</p>}
        </div>
      </section>

      <section className="dfw-group" aria-labelledby="dfw-d-info">
        <h3 className="dfw-group__title" id="dfw-d-info">Instructions <em>optional</em></h3>
        <div className="dfw-field">
          <label className="dfw-label" htmlFor="dfw-d-dtitle">Heading</label>
          <Input id="dfw-d-dtitle" value={texts.form_details_title} onChange={(e) => setText('form_details_title', e.target.value)} placeholder="e.g. Before you apply" />
        </div>
        <div className="dfw-field">
          <div className="dfw-label-row">
            <span className="dfw-label">Text</span>
            <Segmented
              size="small"
              value={texts.form_details_alignment}
              onChange={(v) => setText('form_details_alignment', v)}
              options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centred' }]}
              aria-label="Text alignment"
              disabled={!canEdit}
            />
          </div>
          <RichTextEditor
            value={texts.form_details}
            onChange={(v) => setText('form_details', v)}
            size="details"
            align={texts.form_details_alignment}
            minHeight={160}
            placeholder="What applicants should know before they start"
            aria-label="Instructions text"
            readOnly={!canEdit}
          />
          <p className="dfw-hint">Shown in the gold panel above the questions. Left alignment suits lists best.</p>
        </div>
      </section>

      <section className="dfw-group" aria-labelledby="dfw-d-file">
        <h3 className="dfw-group__title" id="dfw-d-file">File to download <em>optional</em></h3>
        <FileSlot
          id="dfw-d-file-slot"
          label="File"
          hint="PDF or Word, inside the instructions panel"
          value={files.downloadable_file}
          accept=".pdf,.docx"
          onPick={(f) => pickFile('downloadable_file', f)}
          onRemove={() => removeFile('downloadable_file')}
          disabled={!canEdit}
        />
        <div className="dfw-field">
          <label className="dfw-label" htmlFor="dfw-d-ftitle">Heading above the button</label>
          <Input id="dfw-d-ftitle" value={texts.downloadable_file_title} onChange={(e) => setText('downloadable_file_title', e.target.value)} placeholder="e.g. Volunteer guide" />
        </div>
      </section>

      <section className="dfw-group" aria-labelledby="dfw-d-card">
        <h3 className="dfw-group__title" id="dfw-d-card">Application card</h3>
        <p className="dfw-hint dfw-hint--flush">
          The card title (“{language?.application_text || 'Application'}”) and the button (“{language?.submit_text || 'Submit Application'}”)
          come from the form's language, set with Maltese / English in the side panel.
        </p>
      </section>
    </fieldset>
  );

  return (
    <div className={`dfw-design-page${wide ? ' is-wide' : ''}`} ref={rootRef}>
      <div className="dfw-builder__bar">
        <p className="dfw-builder__lede">How the page looks around the questions. Saved changes are live straight away.</p>
        <span className="dfw-grow" />
        {!wide && <Button icon={<EyeOutlined aria-hidden="true" />} onClick={() => setPreviewOpen(true)}>Preview</Button>}
        {liveUrl && <Button href={liveUrl} target="_blank" rel="noopener noreferrer">Open live form</Button>}
      </div>
      <div className="dfw-design-page__cols">
        <div className="dfw-design-page__form">
          {form}
          {canEdit && (
            <div className="dfw-savebar">
              <span className="dfw-savebar__state">{dirty ? 'You have changes that are not saved.' : 'All changes saved.'}</span>
              <span className="dfw-grow" />
              {dirty && (
                <Button onClick={reset}>Undo changes</Button>
              )}
              <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>Save design</Button>
            </div>
          )}
        </div>
        {wide && <div className="dfw-builder__preview"><LivePreview data={preview} defaultDevice={width >= 1400 ? 'desktop' : 'phone'} /></div>}
      </div>
      {!wide && (
        <Drawer
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 24 : 720)}
          title="Preview"
          rootClassName="do-scope dfw-drawer"
          destroyOnHidden
        >
          <LivePreview data={preview} defaultDevice="phone" />
        </Drawer>
      )}
    </div>
  );
};

export default DesignEditor;
